import React, { useState, useEffect } from 'react';
import { ArrowLeft, Save, RotateCcw } from 'lucide-react';
import toast from 'react-hot-toast';
import { statuses } from '../services/apiService';
import { getTalentTypeLabel } from '../utils/talentTypeUtils';
import { Loader } from '../components/UI/LoadingSpinner';

const OrderManagement = () => {
  const [statusesData, setStatusesData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [originalOrder, setOriginalOrder] = useState({});
  const [statusesLoading, setStatusesLoading] = useState(false);

  useEffect(() => {
    loadStatuses();
  }, []);

  // Функция для проверки, изменился ли порядок с неправильного на правильный
  const checkIfOrderChanged = (originalData, normalizedData) => {
    let hasChanges = false;
    
    Object.keys(originalData).forEach(type => {
      const originalStatuses = originalData[type];
      const normalizedStatuses = normalizedData[type];
      
      // Проверяем, отличается ли порядок от нормализованного
      originalStatuses.forEach((originalStatus, index) => {
        const normalizedStatus = normalizedStatuses[index];
        if (originalStatus.id === normalizedStatus.id && 
            originalStatus.display_order !== normalizedStatus.display_order) {
          hasChanges = true;
        }
      });
    });
    
    return hasChanges;
  };

  const loadStatuses = async () => {
    try {
      // Используем общий лоадер только для первой загрузки
      if (!statusesData.length) {
        setLoading(true);
      } else {
        setStatusesLoading(true);
      }
      setError(null);
      // Используем серверную группировку
      const response = await statuses.getAllStatuses({ grouped: 'true', all: true });
      const groupedStatuses = response.data || response;
      
      // Автоматически устанавливаем порядок 1, 2, 3... для каждого типа
      const normalizedStatuses = {};
      Object.keys(groupedStatuses).forEach(type => {
        normalizedStatuses[type] = groupedStatuses[type].map((status, index) => ({
          ...status,
          display_order: index + 1
        }));
      });
      
      setStatusesData(normalizedStatuses);
      
      // Сохраняем оригинальный порядок для отмены изменений
      const originalOrderData = {};
      Object.keys(groupedStatuses).forEach(type => {
        groupedStatuses[type].forEach((status, index) => {
          originalOrderData[status.id] = status.display_order || index + 1;
        });
      });
      setOriginalOrder(originalOrderData);
      
      // Проверяем, изменился ли порядок с неправильного на правильный
      const hasOrderChanges = checkIfOrderChanged(groupedStatuses, normalizedStatuses);
      setHasChanges(hasOrderChanges);
      
      if (hasOrderChanges) {
        console.log('🔄 Порядок статусов изменился с неправильного на правильный');
      } else {
        console.log('✅ Порядок статусов уже правильный (1, 2, 3...)');
      }
      
    } catch (error) {
      console.error('Error loading statuses:', error);
      setError('Error loading statuses');
      toast.error('Error loading statuses: ' + error.message);
    } finally {
      setLoading(false);
      setStatusesLoading(false);
    }
  };

  const moveStatus = (type, fromIndex, toIndex) => {
    if (fromIndex === toIndex) return;

    setStatusesData(prev => {
      const newData = { ...prev };
      const statuses = [...newData[type]];
      const [movedStatus] = statuses.splice(fromIndex, 1);
      statuses.splice(toIndex, 0, movedStatus);
      
      // Обновляем display_order для всех статусов в группе
      const updatedStatuses = statuses.map((status, index) => ({
        ...status,
        display_order: index + 1
      }));
      
      newData[type] = updatedStatuses;
      return newData;
    });
    
    setHasChanges(true);
  };

  const saveOrder = async () => {
    try {
      const updates = [];
      
      // Отправляем все статусы с их текущими порядками
      Object.keys(statusesData).forEach(type => {
        statusesData[type].forEach((status, index) => {
          const newOrder = index + 1;
          updates.push({
            id: status.id,
            display_order: newOrder
          });
        });
      });

      if (updates.length === 0) {
        toast.info('No statuses to save');
        return;
      }

      // Отправляем обновления на сервер
      console.log('Sending updates:', updates);
      await statuses.updateStatusOrder(updates);
      
      setHasChanges(false);
      toast.success('Status order saved successfully!');
      
    } catch (error) {
      console.error('Error saving order:', error);
      toast.error('Error saving order: ' + error.message);
    }
  };

  const resetOrder = () => {
    setStatusesData(prev => {
      const newData = { ...prev };
      Object.keys(newData).forEach(type => {
        newData[type] = newData[type].map(status => ({
          ...status,
          display_order: originalOrder[status.id] || status.display_order
        }));
        newData[type].sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
      });
      return newData;
    });
    setHasChanges(false);
    toast.info('Order reset to original state');
  };

  const getTypeLabel = (type) => {
    // Используем утилиту из talentTypeUtils.js для единообразия по всему проекту
    return getTalentTypeLabel(type);
  };

  // Показываем общий лоадер только для первой загрузки
  if (loading && !statusesData.length) {
    return (
      <Loader
        variant="page"
        text="Loading statuses..."
        size="md"
      />
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4 dark:bg-red-900/30 dark:border-red-800">
        <div className="flex">
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800 dark:text-red-300">Loading Error</h3>
            <div className="mt-2 text-sm text-red-700 dark:text-red-300">
              <p>{error}</p>
            </div>
            <div className="mt-4">
              <button
                onClick={loadStatuses}
                className="bg-red-100 px-3 py-2 rounded-md text-sm font-medium text-red-800 hover:bg-red-200 dark:bg-red-900/50 dark:text-red-300 dark:hover:bg-red-900/70"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <title>Order Management | Talent Service</title>
      {/* Statuses loading overlay */}
      {statusesLoading && (
        <Loader
          variant="component"
          text="Loading statuses..."
          size="md"
        />
      )}

      {/* Header and buttons */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-2 sm:gap-4">
          <button
            onClick={() => window.history.back()}
            className="flex items-center gap-2 px-2 sm:px-3 py-2 text-light-text-secondary dark:text-dark-text-secondary hover:text-light-text-primary dark:hover:text-dark-text-primary transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="hidden sm:inline">Back</span>
          </button>
          <h2 className="text-xl sm:text-2xl font-bold text-light-text-primary dark:text-dark-text-primary">Status Order Management</h2>
        </div>

        <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto sm:gap-3">
          <button
            onClick={resetOrder}
            disabled={!hasChanges}
            className="flex items-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 bg-light-text-tertiary dark:bg-dark-text-tertiary text-white rounded-md hover:bg-light-text-secondary dark:hover:bg-dark-text-secondary transition-colors disabled:bg-light-bg-tertiary dark:disabled:bg-dark-bg-tertiary disabled:cursor-not-allowed text-sm sm:text-base flex-1 sm:flex-none"
          >
            <RotateCcw className="w-4 h-4" />
            Reset
          </button>
          <button
            onClick={saveOrder}
            disabled={!hasChanges}
            className="flex items-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed text-sm sm:text-base flex-1 sm:flex-none"
          >
            <Save className="w-4 h-4" />
            Save
          </button>
        </div>
      </div>

      {/* Instructions */}
      <div className="bg-blue-50 border border-blue-200 rounded-md p-4 dark:bg-blue-900/30 dark:border-blue-800">
        <div className="flex">
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800 dark:text-blue-300">Instructions</h3>
            <div className="mt-2 text-sm text-blue-700 dark:text-blue-300">
              <p>Status order has been automatically normalized (1, 2, 3...). The "Save" button is only enabled when the order changes. Drag cards to change the order.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Status groups */}
      <div className="space-y-8">
        {Object.keys(statusesData).map(type => (
          <div key={type} className="rounded-lg border border-light-border-primary bg-light-bg-primary p-4 shadow-sm dark:border-dark-border-primary dark:bg-dark-bg-secondary sm:p-6">
            <h3 className="text-lg font-semibold text-light-text-primary dark:text-dark-text-primary mb-4">
              {getTypeLabel(type)}
              <span className="ml-2 text-sm text-light-text-tertiary dark:text-dark-text-tertiary">({statusesData[type].length})</span>
            </h3>
            
            <div className="space-y-2">
              {statusesData[type].map((status, index) => (
                <div
                  key={status.id}
                  className="flex min-w-0 flex-wrap items-center gap-4 rounded-lg border border-light-border-primary bg-light-bg-tertiary p-4 transition-colors hover:bg-light-bg-secondary dark:border-dark-border-primary dark:bg-dark-bg-tertiary dark:hover:bg-dark-bg-secondary sm:flex-nowrap"
                  draggable
                  onDragStart={(e) => {
                    e.dataTransfer.setData('text/plain', JSON.stringify({ type, index }));
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    const data = JSON.parse(e.dataTransfer.getData('text/plain'));
                    if (data.type === type && data.index !== index) {
                      moveStatus(type, data.index, index);
                    }
                  }}
                >
                  <div className="flex min-w-0 flex-1 items-start gap-3 sm:items-center">
                    <div className="w-4 h-4 bg-blue-500 rounded-full flex-shrink-0"></div>
                    <div className="min-w-0 flex-1">
                      <h4 className="font-medium text-light-text-primary dark:text-dark-text-primary">{status.name}</h4>
                      <p className="text-sm text-light-text-tertiary dark:text-dark-text-tertiary">{status.description || 'No description'}</p>
                    </div>
                    <div className="text-sm text-light-text-tertiary dark:text-dark-text-tertiary">
                      Order: <span className="font-medium">{status.display_order}</span>
                    </div>
                  </div>
                  
                  {/* Move buttons */}
                  <div className="flex flex-col gap-1">
                    <button
                      onClick={() => moveStatus(type, index, Math.max(0, index - 1))}
                      disabled={index === 0}
                      className="p-1 text-light-text-tertiary dark:text-dark-text-tertiary hover:text-light-text-primary dark:hover:text-dark-text-primary disabled:text-light-text-tertiary dark:disabled:text-dark-text-tertiary disabled:cursor-not-allowed"
                      title="Move up"
                    >
                      ↑
                    </button>
                    <button
                      onClick={() => moveStatus(type, index, Math.min(statusesData[type].length - 1, index + 1))}
                      disabled={index === statusesData[type].length - 1}
                      className="p-1 text-light-text-tertiary dark:text-dark-text-tertiary hover:text-light-text-primary dark:hover:text-dark-text-primary disabled:text-light-text-tertiary dark:disabled:text-dark-text-tertiary disabled:cursor-not-allowed"
                      title="Move down"
                    >
                      ↓
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Changes indicator */}
      {hasChanges && (
        <div className="fixed bottom-36 right-4 z-20 max-w-[calc(100vw-2rem)] rounded-md border border-yellow-300 bg-yellow-100 p-3 shadow-lg dark:border-yellow-800 dark:bg-yellow-900/30">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
            <span className="text-sm text-yellow-800 dark:text-yellow-300">There are unsaved changes</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderManagement;
